// Notion-style block drag handle + drop-line indicator for top-level blocks.
//
// Wired into the editor via DndPlugin's `render.aboveNodes` (see
// usePlateEditorLogic.tsx → createBlockDraggableRender). For every top-level
// element that is "drag-safe" (see isDraggableBlock) this wraps the rendered
// element in a relative container that, on hover, paints a 6-dot gutter handle
// to the left (inside the editor's 96px left page margin) and a blue drop-line
// indicator above/below the block while another block is dragged over it.
//
// Drag mechanics come straight from @platejs/dnd's `useDraggable` /
// `useDropLine` (react-dnd HTML5 backend). The handle is the drag source
// (handleRef + data-block-id, which DndPlugin.onDragStart reads); nodeRef is
// the drop-measurement target.
//
// Clicking (not dragging) the handle opens a small menu — Insert below,
// Duplicate, Delete — implemented with the same editor transforms the slash
// menu / toolbar use (see BlockHandleMenu).
//
// Coexistence with native image file-drop: the drop hook is scoped to the
// internal `block` drag type only (drop.accept = [DRAG_ITEM_BLOCK]) in
// createBlockDraggableRender, so react-dnd does NOT register these blocks as
// OS-file drop targets. OS file drops therefore fall through to
// PlateContent's onDrop={handleDrop} exactly as before.
//
// Tables / columns are deliberately NOT draggable (isDraggableBlock returns
// false) to avoid corrupting their structure against the editor's table /
// column normalizers — they remain valid DROP targets so a paragraph can be
// dropped above/below them.
import { useRef } from 'react';
import { useEditorReadOnly } from 'platejs/react';
import { useDraggable, useDropLine, DRAG_ITEM_BLOCK } from '@platejs/dnd';
import clsx from 'clsx';
import { BlockHandleMenu } from './BlockHandleMenu';
import { isDraggableBlock } from './blockDraggableUtils';
import styles from './BlockDraggable.module.scss';

interface BlockDraggableProps {
    element: any;
    children: React.ReactNode;
}

export function BlockDraggable({ element, children }: BlockDraggableProps) {
    const readOnly = useEditorReadOnly();

    // useDraggable subscribes to react-dnd; only call it when this block is
    // actually draggable so non-draggable blocks (tables/columns, nested
    // blocks) don't register a drag source. Hook order is kept stable because
    // isDraggableBlock is a pure function of the (stable-per-render) element.
    if (readOnly || !isDraggableBlock(element)) {
        return <>{children}</>;
    }

    return <DraggableBlockInner element={element}>{children}</DraggableBlockInner>;
}

function DraggableBlockInner({ element, children }: BlockDraggableProps) {
    const blockId: string = element.id;
    // drop.accept is scoped to the internal `block` type ONLY (default would
    // also accept NativeTypes.FILE). This is what guarantees coexistence with
    // PlateContent's native onDrop={handleDrop}: because these blocks are NOT
    // registered as react-dnd FILE drop targets, OS image file drops are not
    // intercepted by the backend and fall through to handleDrop as before.
    const { nodeRef, previewRef, handleRef, isDragging } = useDraggable({
        element,
        drop: { accept: [DRAG_ITEM_BLOCK] },
    });
    const { dropLine } = useDropLine({ id: blockId });
    const menuAnchorRef = useRef<HTMLDivElement>(null);

    return (
        <div ref={nodeRef} className={clsx(styles.block, isDragging && styles.blockDragging)} data-block-draggable={blockId}>
            <div className={styles.gutter} contentEditable={false} ref={menuAnchorRef}>
                <BlockHandleMenu blockId={blockId} element={element} handleRef={handleRef} anchorRef={menuAnchorRef} />
            </div>

            {/* Multiple-selection drag preview target — kept empty/hidden; the
                default single-block preview is the browser's drag image. */}
            <div ref={previewRef} className={styles.previewSlot} contentEditable={false} aria-hidden />

            <div className={styles.blockContent}>{children}</div>

            {dropLine ? (
                <div
                    className={clsx(styles.dropLine, dropLine === 'top' ? styles.dropLineTop : styles.dropLineBottom)}
                    contentEditable={false}
                />
            ) : null}
        </div>
    );
}
